import {
  CARESEEKER_STATUS_ACTIVE,
  CARESEEKER_STATUS_CREATED,
  CARESEEKER_STATUS_INACTIVE,
  CARESEEKER_STATUS_LOST,
  CARESEEKER_STATUS_ONBOARDING,
  PRODUCT_FEATURES,
} from "core/consts";
import { Careseeker, SearchType } from "core/types";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { getProductFeatureActive } from "../productfeatures";
import { ACCENT_COLOR, ERROR_COLOR, SUCCESS_COLOR } from "../utils/colors";
import {
  activateNewFormDesign,
  activateSendToOperatorAgent,
} from "../utils/featureFlags";

export const OPERATOR_EXECUTOR_PORT = 8765;

export function defaultSearchType(
  careseeker: Careseeker | undefined,
): SearchType | null {
  if (careseeker?.patient_types?.length == 1)
    return careseeker?.patient_types[0];

  return null;
}

export function getCareseekerStatusColor(status: number | undefined) {
  switch (status) {
    case CARESEEKER_STATUS_ACTIVE:
      return SUCCESS_COLOR;
    case CARESEEKER_STATUS_INACTIVE:
    case CARESEEKER_STATUS_LOST:
      return ERROR_COLOR;
    case CARESEEKER_STATUS_ONBOARDING:
      return ACCENT_COLOR;
    case CARESEEKER_STATUS_CREATED:
      return null;
    default:
      return null;
  }
}

export function onPremHttpRequest({
  body,
  headers: extraHeaders,
  method,
  on_premise_authorization_token,
  on_premise_domain_ssl,
  path,
}: {
  body?: BodyInit;
  headers?: HeadersInit;
  method: string;
  on_premise_authorization_token: string | undefined;
  on_premise_domain_ssl: string | undefined;
  path: string;
}) {
  const headers: Record<string, string> = {
    ...(extraHeaders as Record<string, string>),
  };

  if (on_premise_authorization_token) {
    // username is empty.
    const auth = btoa(`:${on_premise_authorization_token}`);
    headers["Authorization"] = `Basic ${auth}`;
  }

  return fetch(`https://${on_premise_domain_ssl}/${path}`, {
    body,
    headers,
    method,
  });
}

export async function mintTestPatient({
  on_premise_authorization_token,
  on_premise_domain_ssl,
  template,
}: {
  on_premise_authorization_token: string | undefined;
  on_premise_domain_ssl: string | undefined;
  template: string;
}): Promise<{ external_id: string } | null> {
  try {
    if (!on_premise_authorization_token || !on_premise_domain_ssl) {
      return null;
    }
    const response = await onPremHttpRequest({
      body: JSON.stringify({ template }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
      on_premise_authorization_token,
      on_premise_domain_ssl,
      path: "test/patient/create",
    });
    if (response?.ok) {
      const data = await response.json();
      return data as { external_id: string };
    }
  } catch {
    return null;
  }
  return null;
}

export function hasSSOActivated(careseeker: Careseeker | undefined): boolean {
  const ssoIntegration = careseeker?.sso_integration;
  if (!ssoIntegration) return false;

  return ssoIntegration?.active;
}

export function hasSSOConsentGiven(
  careseeker: Careseeker | undefined,
): boolean {
  const ssoIntegration = careseeker?.sso_integration;
  if (!ssoIntegration) return false;

  return !!(
    ssoIntegration?.consent ?? ssoIntegration?.config?.deactivate_consent_flow
  );
}

export function hasSSOActivatedAndConsentGiven(
  careseeker: Careseeker | undefined,
): boolean {
  const ssoIntegration = careseeker?.sso_integration;
  if (!ssoIntegration) return false;

  return hasSSOActivated(careseeker) && hasSSOConsentGiven(careseeker);
}

export const useIsDocsAndExtractActive = () => {
  const careseeker = useLoggedCareseeker();
  const isDocsAndExtractActive = getProductFeatureActive(
    careseeker?.product_features,
    PRODUCT_FEATURES.DOCS_AND_EXTRACT,
  );
  const isDischargeLetterActive = getProductFeatureActive(
    careseeker?.product_features,
    PRODUCT_FEATURES.DISCHARGE_LETTER,
  );

  return {
    isDischargeLetterActive,
    isDocsAndExtractActive,
    isRedesignActive:
      activateNewFormDesign() ||
      isDocsAndExtractActive ||
      isDischargeLetterActive,
  } as const;
};

export const useOperatorAgent = (): {
  active: boolean;
  port: number;
} => {
  const careseeker = useLoggedCareseeker();
  const active =
    activateSendToOperatorAgent &&
    getProductFeatureActive(
      careseeker?.product_features,
      PRODUCT_FEATURES.OPERATOR_AGENT,
    );

  return {
    active,
    port: careseeker?.config?.operator_agent_port ?? OPERATOR_EXECUTOR_PORT,
  };
};
